import execa, { ExecaChildProcess } from 'execa';
import { exitWhenParentExits, log } from '../../utils';
import { killProcessTree, pipeStdout, watchStdout } from '../utils';
import { Task, TaskParameters } from './task';

export interface Indicators {
    end: string;
    watchStart: string;
    watchEnd?: string;
}

export interface CliTaskParameters extends TaskParameters {
    indicators: Indicators;
}

export abstract class CliTask extends Task {
    private readonly indicators: Indicators;

    constructor({ indicators, ...rest }: CliTaskParameters) {
        super(rest);
        this.indicators = indicators;
    }

    watchForParentExit(): void {
        // No-op: this task exits automatically when its child process ends
    }

    protected async runChildProcess(command: string, args: string[]) {
        const childProcess = execa(
            command,
            args,
            this.watch
                ? {
                      stdio: ['inherit', 'pipe', 'inherit'],
                      /*
                       * Put the child in its own process group so we can take down bin
                       * wrappers that fork children of their own. Not needed on Windows.
                       */
                      detached: process.platform !== 'win32',
                  }
                : {
                      stdio: 'inherit',
                  }
        );

        if (!this.watch) {
            try {
                await childProcess;
            } finally {
                this.logCompletionResults(this.taskTimer.end());
            }
            return;
        }

        pipeStdout(childProcess);
        watchStdout(childProcess, this.indicators.watchStart, () => {
            this.taskTimer.start();
        });
        watchStdout(childProcess, this.indicators.end, () => {
            this.logCompletionResults(this.taskTimer.end());
        });
        watchStdout(childProcess, this.indicators.watchEnd, () => {
            this.logCompletionResults(this.taskTimer.end());
        });

        this.killOnParentExit(childProcess);
        // Forward CTRL-C when signalled, rather than wait for killOnParentExit's slower poll.
        this.forwardSignalToChild(childProcess);

        await childProcess;
    }

    private forwardSignalToChild(childProcess: ExecaChildProcess) {
        if (process.platform === 'win32') {
            return;
        }

        const forward = (signal: NodeJS.Signals) => {
            if (childProcess.pid) {
                log.info(`${this.name} forwarding signal to child process tree`);
                killProcessTree(childProcess.pid, signal);
            }
            // Re-raise to get Node's default behavior
            process.kill(process.pid, signal);
        };

        process.once('SIGINT', () => forward('SIGINT'));
        process.once('SIGTERM', () => forward('SIGTERM'));
    }

    private killOnParentExit(childProcess: ExecaChildProcess) {
        exitWhenParentExits(this.name, () => {
            if (childProcess.pid) {
                log.info(`${this.name} killing child process tree`);
                killProcessTree(childProcess.pid);
            }
        });
    }
}
